import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Tooltip, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import moment from 'moment';
import { SecurityRead } from 'api';
import {
  EMPTY_OPTION_POOL_REFRESH,
  getNewRoundSecurityNameTakenMessage,
  NEW_ROUND_CURRENCY_BADGE_LABEL,
  NEW_ROUND_CURRENCY_BADGE_TOOLTIP,
  NEW_ROUND_FUND_INVESTMENT_EXCEEDS_AMOUNT_ERROR,
  NEW_ROUND_MODELER_DESCRIPTION,
  NEW_ROUND_MODELER_TITLE,
  NEW_ROUND_NO_PREFERRED_SECURITY_ERROR,
  NEW_ROUND_NUMBER_FORMAT_BADGE_LABEL,
  OPR_DECIMAL_PLACES,
  POOL_REFRESH_MODE_PRE,
} from 'common/constants/cap-table';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { NewRoundModelerProps, PoolRefreshMode } from './NewRoundModeler.props';
import {
  buildNewRoundModelerData,
  computeNewRoundModeler,
  formatInvestmentDate,
  getCurrentSharesOutstanding,
  getDefaultFundId,
  getDefaultOptionPoolRefresh,
  getNextSeriesName,
  getPremoneyUnallocatedOptions,
  hasExistingPreferredSecurity,
  isFundInvestmentExceedingAmountInvested,
  isNewRoundModelerValid,
  isSecurityNameTaken,
} from './NewRoundModeler.utils';
import createCalculationsSheetConfig from './sheets/createCalculationsSheetConfig';
import createFundOwnershipSheetConfig from './sheets/createFundOwnershipSheetConfig';
import createInputsSheetConfig from './sheets/createInputsSheetConfig';
import { NrmTableData } from './sheets/types';

const SLASH_DATE_FORMAT = 'MM/DD/YYYY';

// Column widths come from each cell's own `style` (sheetCellHelpers.tsx / VALUE_STYLE),
// NOT from ScalarSpreadsheet's `columnWidths`/`columnTypes` props. Those route every cell
// through applyResizeToGrid's `{...cell, style: {...}}` spread (gridColumnTransformers.tsx),
// which drops the Cell class's prototype methods - the next edit on that cell then throws
// "changeCell.onChange is not a function" inside useWorkbook.onChange.
// Grid cell text renders at the library default (14px) regardless of gridType; scoped
// to just these three sheets so long labels/large share counts fit without touching
// font size anywhere else in the app.
const NRM_CELL_FONT_SIZE_SX = { '& .data-grid td': { fontSize: '0.75rem' } };

const NewRoundModeler = ({ captableInfo, fundList, onChange }: NewRoundModelerProps) => {
  const [expanded, setExpanded] = useState(false);

  const securities: SecurityRead[] = useMemo(() => (captableInfo?.securities ?? []) as SecurityRead[], [captableInfo?.securities]);

  const cso = useMemo(() => getCurrentSharesOutstanding(captableInfo?.shares_diluted), [captableInfo?.shares_diluted]);

  const puo = useMemo(() => getPremoneyUnallocatedOptions(securities), [securities]);

  const defaultOPR = useMemo(() => getDefaultOptionPoolRefresh(cso, puo), [cso, puo]);
  const defaultSecurityName = useMemo(() => getNextSeriesName(securities), [securities]);
  const defaultFundId = useMemo(
    () => getDefaultFundId(captableInfo?.fund_ownership, fundList),
    [captableInfo?.fund_ownership, fundList]
  );

  const [preMoney, setPreMoney] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [optionPoolRefresh, setOptionPoolRefresh] = useState(defaultOPR);
  const [poolRefreshMode, setPoolRefreshMode] = useState<PoolRefreshMode>(POOL_REFRESH_MODE_PRE);
  const [securityName, setSecurityName] = useState(defaultSecurityName);
  const [investmentDate, setInvestmentDate] = useState<Date | null>(new Date());
  const [fundId, setFundId] = useState<number | null>(defaultFundId);
  const [fundInvestment, setFundInvestment] = useState('');
  const [fundInvestmentTouched, setFundInvestmentTouched] = useState(false);

  useEffect(() => {
    if (!optionPoolRefresh || optionPoolRefresh === EMPTY_OPTION_POOL_REFRESH) {
      setOptionPoolRefresh(defaultOPR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOPR]);

  useEffect(() => {
    setSecurityName(prev => prev || defaultSecurityName);
  }, [defaultSecurityName]);

  useEffect(() => {
    setFundId(prev => prev ?? defaultFundId);
  }, [defaultFundId]);

  // Mirror fundInvestment to amountInvested until the user manually edits it
  useEffect(() => {
    if (!fundInvestmentTouched) {
      setFundInvestment(amountInvested);
    }
  }, [amountInvested, fundInvestmentTouched]);

  const computed = useMemo(
    () =>
      computeNewRoundModeler({
        preMoney,
        amountInvested,
        optionPoolRefresh,
        poolRefreshMode,
        fundInvestment,
        cso,
        puo,
      }),
    [preMoney, amountInvested, optionPoolRefresh, poolRefreshMode, fundInvestment, cso, puo]
  );

  const hasPreferredSecurity = useMemo(() => hasExistingPreferredSecurity(securities), [securities]);

  const nameTaken = useMemo(() => isSecurityNameTaken(securities, securityName), [securities, securityName]);

  const fundInvestmentExceedsAmount = useMemo(
    () => isFundInvestmentExceedingAmountInvested(fundInvestment, amountInvested),
    [fundInvestment, amountInvested]
  );

  const isModelerValid = useMemo(
    () =>
      isNewRoundModelerValid({
        expanded,
        preMoney,
        amountInvested,
        fundInvestment,
        optionPoolRefresh,
        securityName,
        isNameTaken: nameTaken,
        hasPreferredSecurity,
        fundId,
        investmentDate,
      }),
    [
      expanded,
      preMoney,
      amountInvested,
      fundInvestment,
      optionPoolRefresh,
      securityName,
      nameTaken,
      hasPreferredSecurity,
      fundId,
      investmentDate,
    ]
  );

  const investmentDateStr = useMemo(() => formatInvestmentDate(investmentDate), [investmentDate]);

  const buildData = useCallback(
    () =>
      buildNewRoundModelerData({
        expanded,
        isModelerValid,
        preMoney,
        amountInvested,
        optionPoolRefresh,
        poolRefreshMode,
        securityName,
        investmentDateStr,
        fundId,
        fundInvestment,
        computed,
      }),
    [expanded, isModelerValid, preMoney, amountInvested, optionPoolRefresh, poolRefreshMode, securityName, investmentDateStr, fundId, fundInvestment, computed]
  );

  useEffect(() => {
    onChange(buildData());
  }, [buildData, onChange]);

  const tableData: NrmTableData = useMemo(
    () => ({
      preMoney,
      amountInvested,
      optionPoolRefresh,
      poolRefreshMode,
      securityName,
      investmentDate: investmentDateStr,
      fundId,
      fundInvestment,
      fundList: fundList ?? [],
      cso,
      puo,
      computed,
    }),
    [preMoney, amountInvested, optionPoolRefresh, poolRefreshMode, securityName, investmentDateStr, fundId, fundInvestment, fundList, cso, puo, computed]
  );

  // Rebuilt whenever tableData changes - same pattern Waterfall's useCreateInputSheet uses
  // (a fresh SpreadsheetConfig per edit); useWorkbook's own effect picks up the new
  // `sheets` array identity and re-evaluates. This is NOT what broke Enter-to-commit -
  // the actual cause was the `columnWidths` prop (see NRM_CELL_FONT_SIZE_SX comment
  // above and sheetCellHelpers.tsx), now removed.
  const inputsSheet = useMemo(() => createInputsSheetConfig(tableData), [tableData]);
  const fundOwnershipSheet = useMemo(() => createFundOwnershipSheetConfig(tableData), [tableData]);
  const calculationsSheet = useMemo(() => createCalculationsSheetConfig(tableData), [tableData]);

  const sheets = useMemo(
    () => [inputsSheet, fundOwnershipSheet, calculationsSheet],
    [inputsSheet, fundOwnershipSheet, calculationsSheet]
  );

  const { onChange: workbookOnChange } = useWorkbook(sheets);

  const handleCellChange = useCallback(
    (cell: any, value: any) => {
      const scrollEl = accordionRef.current?.closest('.MuiDialogContent-root') as HTMLElement | null;
      const savedScrollTop = scrollEl?.scrollTop ?? 0;

      workbookOnChange(cell, value);
      switch (cell.alias) {
        case 'preMoney':
          setPreMoney(String(value ?? ''));
          break;
        case 'amountInvested':
          setAmountInvested(String(value ?? ''));
          break;
        case 'optionPoolRefresh':
          // The cell stores/emits a fraction (ValueEditor's percentage convention);
          // NRM's own state/calc functions expect a percent-number string ("9.7800").
          setOptionPoolRefresh(value != null && value !== '' ? (Number(value) * 100).toFixed(OPR_DECIMAL_PLACES) : '');
          break;
        case 'poolRefreshMode':
          setPoolRefreshMode(value as PoolRefreshMode);
          break;
        case 'securityName':
          setSecurityName(String(value ?? ''));
          break;
        case 'investmentDate':
          setInvestmentDate(value ? moment(value as string, SLASH_DATE_FORMAT).toDate() : null);
          break;
        case 'fundId':
          setFundId(value != null && value !== '' ? Number(value) : null);
          break;
        case 'fundInvestment':
          setFundInvestmentTouched(true);
          setFundInvestment(String(value ?? ''));
          break;
        default:
          break;
      }

      if (scrollEl && savedScrollTop > 0) {
        requestAnimationFrame(() => {
          scrollEl.scrollTop = savedScrollTop;
        });
      }
    },
    [workbookOnChange]
  );

  const accordionRef = useRef<HTMLDivElement>(null);

  return (
    <Box ref={accordionRef} sx={{ mt: 1 }}>
      <Accordion
        expanded={expanded}
        onChange={(_, v) => setExpanded(v)}
        disableGutters
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary
          expandIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            borderBottom: expanded ? '1px solid' : 'none',
            borderColor: 'divider',
            minHeight: 48,
            '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between' },
          }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5, fontSize: '0.85rem' }}>
            {NEW_ROUND_MODELER_TITLE}
          </Typography>
          <Tooltip title={NEW_ROUND_CURRENCY_BADGE_TOOLTIP} placement="top">
            <Box sx={{ display: 'flex', mr: 1 }}>
              <Button
                disableRipple
                tabIndex={-1}
                variant="outlined"
                size="small"
                sx={{
                  height: 19,
                  fontSize: 12,
                  fontWeight: 800,
                  px: '14px',
                  py: 0,
                  minWidth: 'auto',
                  lineHeight: 'inherit',
                  borderRadius: 0,
                  color: 'success.main',
                  borderColor: 'success.main',
                  '&:hover': { backgroundColor: 'transparent' },
                }}>
                {NEW_ROUND_CURRENCY_BADGE_LABEL}
              </Button>
              <Button
                disableRipple
                tabIndex={-1}
                variant="outlined"
                size="small"
                sx={{
                  height: 19,
                  fontSize: 12,
                  fontWeight: 800,
                  px: '14px',
                  py: 0,
                  minWidth: 'auto',
                  lineHeight: 'inherit',
                  borderRadius: 0,
                  ml: '-1px',
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '&:hover': { backgroundColor: 'transparent' },
                }}>
                {NEW_ROUND_NUMBER_FORMAT_BADGE_LABEL}
              </Button>
            </Box>
          </Tooltip>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.78rem' }}>
            {NEW_ROUND_MODELER_DESCRIPTION}
          </Typography>

          {!hasPreferredSecurity && (
            <Typography variant="body2" sx={{ mb: 2, color: 'error.main', fontSize: '0.78rem' }}>
              {NEW_ROUND_NO_PREFERRED_SECURITY_ERROR}
            </Typography>
          )}
          {nameTaken && (
            <Typography variant="body2" sx={{ mb: 2, color: 'error.main', fontSize: '0.78rem' }}>
              {getNewRoundSecurityNameTakenMessage(securityName.trim())}
            </Typography>
          )}
          {fundInvestmentExceedsAmount && (
            <Typography variant="body2" sx={{ mb: 2, color: 'error.main', fontSize: '0.78rem' }}>
              {NEW_ROUND_FUND_INVESTMENT_EXCEEDS_AMOUNT_ERROR}
            </Typography>
          )}

          <Grid container spacing={2} sx={NRM_CELL_FONT_SIZE_SX}>
            {/* Left Column */}
            <Grid size={6} sx={{ overflow: 'hidden' }}>
              {/* New Round Inputs - row 1 of the sheet itself is the dark header bar
                  (sectionHeaderRow in sheetCellHelpers), so it's always exactly the
                  grid's width instead of a separately-sized MUI <Table>. */}
              <ScalarSpreadsheet
                {...(inputsSheet as any)}
                sheet={inputsSheet as any}
                // Explicit on top of the spread - class-instance spread does not reliably
                // hand these to React as their own prop identities, so after an in-place
                // sheet.reset()+evaluate() the grid can stay stuck on stale/skeleton data
                // (see the same note in WaterfallTables.tsx).
                data={(inputsSheet as any)?.data ?? []}
                totalData={(inputsSheet as any)?.totalData ?? []}
                titleData={(inputsSheet as any)?.titleData ?? []}
                onChange={handleCellChange}
                showToolbar={false}
                displayRowIndicator={false}
                displayColumnIndicator={false}
                alwaysDisplayLegend={false}
              />

              {/* New Round Fund Ownership */}
              <Box sx={{ mt: 1 }}>
                <ScalarSpreadsheet
                  {...(fundOwnershipSheet as any)}
                  sheet={fundOwnershipSheet as any}
                  data={(fundOwnershipSheet as any)?.data ?? []}
                  totalData={(fundOwnershipSheet as any)?.totalData ?? []}
                  titleData={(fundOwnershipSheet as any)?.titleData ?? []}
                  onChange={handleCellChange}
                  showToolbar={false}
                  displayRowIndicator={false}
                  displayColumnIndicator={false}
                  alwaysDisplayLegend={false}
                />
              </Box>
            </Grid>

            {/* Right Column */}
            <Grid size={6} sx={{ overflow: 'hidden' }}>
              <ScalarSpreadsheet
                {...(calculationsSheet as any)}
                sheet={calculationsSheet as any}
                data={(calculationsSheet as any)?.data ?? []}
                totalData={(calculationsSheet as any)?.totalData ?? []}
                titleData={(calculationsSheet as any)?.titleData ?? []}
                onChange={handleCellChange}
                showToolbar={false}
                displayRowIndicator={false}
                displayColumnIndicator={false}
                alwaysDisplayLegend={false}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default NewRoundModeler;
